import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Text,
    Toolbar,
    ToolbarButton,
    Dropdown,
    Option,
    Field,
    Radio, RadioGroup,
    Button
} from '@fluentui/react-components';
import { DocumentData20Regular, ArrowLeft20Regular, ArrowDownload20Regular } from '@fluentui/react-icons';
import { useAppStyles } from '../../styles/appStyles';
import { useReportsPageStyles } from '../../styles/reportsPageStyles';
import EupsSelector from '../EupsSelector';
import ProgressPage from "../ProgressPage";
import { useKpoData } from "../../hooks/useKpoData";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { config } from "../../../config/environment";
import { EupType } from "../../../model";
import { useDevice } from '../../../model/DeviceContext';

const appInsights = new ApplicationInsights({
    config: { instrumentationKey: config.appInsights.instrumentationKey }
});
appInsights.loadAppInsights();

interface ReportsControlCardKPOProps {
    onBack: () => void;
    companyId: string | null;
}

const ReportsControlCardKPO: React.FC<ReportsControlCardKPOProps> = ({ onBack, companyId }) => {
    const { t } = useTranslation();
    const styles = useAppStyles();
    const reportStyles = useReportsPageStyles();
    const { subscription, isTrial } = useDevice();
    const ReportId = "CW02";

    console.log('📊 ReportsControlCardKPO RENDER:', {
        companyId,
        hasCompanyId: !!companyId,
        subscriptionCompanies: subscription?.companies?.length || 0
    });

    // Stany formularza
    const [selectedEupId, setSelectedEupId] = useState<string | null>(null);
    const [selectedRadio, setSelectedRadio] = useState<string>("");
    const [dateFrom, setDateFrom] = useState<Date | null>(null);
    const [dateTo, setDateTo] = useState<Date | null>(null);
    const [dateError, setDateError] = useState<string | null>(null);
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState<string>("");

    // Handler dla zmiany EUP - automatycznie ustawia selected
    const handleEupChange = (eupId: string | null) => {
        setSelectedEupId(eupId);
    };
    // Hook dla KPO data - podobnie jak useWasteProcessData w App.tsx
    const {
        showProgress,
        fetchDataFunction,
        fetchKpoData,
        handleProgressComplete,
    } = useKpoData(() => {
        // Callback wywoływany po zakończeniu - można zostawić pusty lub dodać logikę
        console.log('KPO report generation completed');
    });

    // Funkcja obsługująca generowanie raportu
    const handleGenerateKpoReport = () => {
        if (!selectedEupId || !selectedRadio) {
            setDateError("Wybierz punkt odbioru i zakres czasowy");
            return;
        }
        // Znajdź dane firmy i EUP
        const company = subscription?.companies?.find(c => c.companyId === companyId);
        const eup = company?.eups?.find(e => e.eupId === selectedEupId);

        if (!company || !eup) {
            console.error("Nie znaleziono danych firmy lub EUP");
            return;
        }

        // Utworzenie eupType na podstawie danych z subscription
        const eupType: EupType = {
            bdoentityname: company.name, // Dodane pole
            eupId: eup.eupId,
            identificationNumber: eup.identificationNumber,
            name: eup.name,
            clientId: company.clientId,
            clientSecret: company.clientSecret,
            isTrial: isTrial
        };

        let finalDateFrom: Date;
        let finalDateTo: Date;
        let sheetName: string;

        // Ustal daty na podstawie wybranej opcji radio
        if (selectedRadio === "A") {
            // Bieżący miesiąc
            const currentDate = new Date();
            finalDateFrom = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1); // 1 dnia bieżącego miesiąca
            finalDateTo = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0); // Ostatni dzień bieżącego miesiąca
            sheetName = `${eupType.identificationNumber.replace(/\//g, "|")} ${ReportId} ${(currentDate.getMonth() + 1).toString().padStart(2, "0")}'${currentDate.getFullYear().toString().slice(-2)}`;

        } else if (selectedRadio === "B") {
            // Poprzedni miesiąc
            const currentDate = new Date();
            const previousMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
            finalDateFrom = new Date(previousMonth.getFullYear(), previousMonth.getMonth(), 1); // 1 dnia poprzedniego miesiąca
            finalDateTo = new Date(previousMonth.getFullYear(), previousMonth.getMonth() + 1, 0); // Ostatni dzień poprzedniego miesiąca
            sheetName = `${eupType.identificationNumber.replace(/\//g, "|")} ${ReportId} ${(previousMonth.getMonth() + 1).toString().padStart(2, "0")}'${previousMonth.getFullYear().toString().slice(-2)}`;
        } else if (selectedRadio === "C" && selectedYear && selectedMonth) {
            // Niestandardowy zakres dat - cały wybrany miesiąc lub cały rok
            if (dateError) {
                return; // Nie kontynuuj jeśli są błędy walidacji
            }

            if (selectedMonth === "0") {
                // Wszystkie miesiące - cały rok
                finalDateFrom = new Date(selectedYear, 0, 1); // 1 stycznia
                finalDateTo = new Date(selectedYear, 11, 31); // 31 grudnia
                const yearStr = selectedYear.toString().slice(-2);
                sheetName = `${eupType.identificationNumber.replace(/\//g, "|")} ${ReportId} '${yearStr}`;
            } else {
                // Konkretny miesiąc
                const monthNum = parseInt(selectedMonth);
                finalDateFrom = new Date(selectedYear, monthNum - 1, 1); // Pierwszy dzień miesiąca
                finalDateTo = new Date(selectedYear, monthNum, 0); // Ostatni dzień miesiąca
                const monthStr = monthNum.toString().padStart(2, "0");
                const yearStr = selectedYear.toString().slice(-2);
                sheetName = `${eupType.identificationNumber.replace(/\//g, "|")} ${ReportId} ${monthStr}'${yearStr}`;
            }
        } else {
            console.error("Nieprawidłowe parametry dla generowania raportu");
            return;
        }


        // Wywołaj funkcję pobierania danych
        fetchKpoData(
            eupType,
            finalDateFrom,
            finalDateTo,
            sheetName,
        );
    };


    // Sprawdź czy można włączyć przycisk generowania
    const canGenerate = selectedEupId && selectedRadio &&
        (selectedRadio !== "C" || (selectedYear && selectedMonth && !dateError));

    // Jeśli pokazujemy progress, renderuj ProgressPage
    if (showProgress && fetchDataFunction) {
        // Pobierz nazwę EUP dla wyświetlenia
        const company = subscription?.companies?.find(c => c.companyId === companyId);
        const eup = company?.eups?.find(e => e.eupId === selectedEupId);
        const eupDisplayName = eup ? `${eup.identificationNumber} - ${eup.name}` : undefined;

        return (
            <ProgressPage
                fetchData={fetchDataFunction}
                loadingMessage="Generowanie raportu KPO..."
                successMessage={t("report_success_kpo_generated")}
                backTo="/"
                autoReturnDelay={3000}
                onComplete={handleProgressComplete}
                reportCode="CW02"
                reportName={t('AccordionHeader_control_reports_option1', 'Potwierdzone KPO')}
                eupName={eupDisplayName}
                additionalInfo="Generowanie raportu może potrwać dłuższą chwilę..."
            />
        );
    }
    return (
        <div className={styles.appPageContainer}>
            <div className={styles.appPageHeader}>
                <DocumentData20Regular className={styles.appPageHeaderIcon} />
                <Text className={styles.appPageTitle}>
                    {ReportId} - {t('AccordionHeader_control_reports_option1', 'Potwierdzone KPO')}
                </Text>
            </div>

            <Toolbar className={styles.toolbar}>
                <ToolbarButton
                    className={styles.toolbarButton}
                    aria-label={t('common.back', 'Powrót')}
                    icon={<ArrowLeft20Regular className={styles.backIcon} />}
                    onClick={onBack}
                >
                    {t('common.back', 'Powrót')}
                </ToolbarButton>
            </Toolbar>

            {companyId && (
                <EupsSelector
                    companyId={companyId}
                    onEupChange={handleEupChange}
                    initialSelectedEupId={selectedEupId}
                />
            )}

            <div className={reportStyles.content}>
                <Field label={t("ReportsControlCard_Option1_Radio_Header")}>
                    <RadioGroup
                        value={selectedRadio} // Upewnij się, że wartość jest kontrolowana
                        onChange={(_, data) => {
                            setSelectedRadio(data.value); // Ustaw wartość wybranego radio
                            setDateError(null); // Czyść błędy daty
                            if (data.value !== "C") {
                                setSelectedMonth(""); // Reset month when not using custom option
                            }
                        }}
                        disabled={!selectedEupId}

                    >
                        <Radio value="A" label={t("ReportsControlCard_Option1_Radio_A")} />
                        <Radio value="B" label={t("ReportsControlCard_Option1_Radio_B")} />
                        <Radio value="C" label={
                            <>
                                {t("ReportsControlCard_Option1_Radio_C")}
                                <br />
                                <Text size={200}>{t("ReportsControlCard_Option1_Radio_C_Subtext")}</Text>
                                <div style={{ marginTop: "12px", opacity: selectedRadio !== "C" ? 0.6 : 1 }}>
                                    <Field label="Rok">
                                        <Dropdown
                                            placeholder="Wybierz rok"
                                            size="small"
                                            disabled={selectedRadio !== "C"}
                                            value={selectedYear ? selectedYear.toString() : ""}
                                            onOptionSelect={(_, data) => {
                                                setSelectedYear(parseInt(data.optionValue as string));
                                                setSelectedMonth(""); // Reset month when year changes
                                            }}
                                        >
                                            {(() => {
                                                const currentYear = new Date().getFullYear();
                                                const years = [];
                                                for (let year = 2019; year <= currentYear; year++) {
                                                    years.push(year);
                                                }
                                                return years.map(year => (
                                                    <Option key={year} value={year.toString()}>
                                                        {year}
                                                    </Option>
                                                ));
                                            })()}
                                        </Dropdown>
                                    </Field>
                                </div>
                                <div style={{ marginTop: "8px", opacity: selectedRadio !== "C" || !selectedYear ? 0.6 : 1 }}>
                                    <Field label={t("form_label_month")}>
                                        <Dropdown
                                            placeholder={t("form_placeholder_select_month")}
                                            size="small"
                                            disabled={selectedRadio !== "C" || !selectedYear}
                                            value={selectedMonth ? (() => {
                                                if (selectedMonth === "0") return t("form_option_all_months");
                                                const months = [
                                                    t("month_january"), t("month_february"), t("month_march"), t("month_april"), t("month_may"), t("month_june"),
                                                    t("month_july"), t("month_august"), t("month_september"), t("month_october"), t("month_november"), t("month_december")
                                                ];
                                                const monthIndex = parseInt(selectedMonth) - 1;
                                                return months[monthIndex] || "";
                                            })() : ""}
                                            onOptionSelect={(_, data) => {
                                                setSelectedMonth(data.optionValue as string);
                                            }}
                                        >
                                            {(() => {
                                                if (!selectedYear) return [];
                                                const currentDate = new Date();
                                                const currentYear = currentDate.getFullYear();
                                                const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11
                                                const maxMonth = selectedYear === currentYear ? currentMonth : 12;

                                                const months = [
                                                    t("month_january"), t("month_february"), t("month_march"), t("month_april"), t("month_may"), t("month_june"),
                                                    t("month_july"), t("month_august"), t("month_september"), t("month_october"), t("month_november"), t("month_december")
                                                ];

                                                const options = [
                                                    <Option key={0} value="0">
                                                        {t("form_option_all_months")}
                                                    </Option>
                                                ];

                                                options.push(...months.slice(0, maxMonth).map((month, index) => (
                                                    <Option key={index + 1} value={(index + 1).toString()}>
                                                        {month}
                                                    </Option>
                                                )));

                                                return options;
                                            })()}
                                        </Dropdown>
                                    </Field>
                                </div>

                            </>
                        }
                        />
                    </RadioGroup>
                    {dateError && (
                        <Text style={{ color: "red" }}>{dateError}</Text>
                    )}
                </Field>
                <div className={reportStyles.buttonWrapper}>
                    <Button
                        appearance="primary"
                        className={styles.buttonBottom}
                        disabled={!canGenerate}
                        onClick={handleGenerateKpoReport}
                        icon={<ArrowDownload20Regular />}
                    >
                        {t("ReportsControlCard_button_generate")}
                    </Button>
                </div>
            </div>

        </div>
    );
};

export default ReportsControlCardKPO;        